import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { ThemeService } from '@ld.consumer-portal/utils';

export interface MigrationNewPasswordData {
  email: string;
  newPassword: string;
}

@Component({
  selector: 'ld-migration-password-complexity-card',
  imports: [FormsModule, MatIconModule, TranslocoDirective],
  templateUrl: './migration-password-complexity-card.component.html',
  styleUrl: './migration-password-complexity-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MigrationPasswordComplexityCardComponent {
  private readonly themeService = inject(ThemeService);

  protected logoUrl = computed(() => this.themeService.getLogoUrl());
  protected partnerDisplayName = computed(() => this.themeService.getPartnerDisplayName());

  email = input('');
  isLoading = input(false);
  externalErrorMessage = input<string | null>(null);

  newPasswordSubmit = output<MigrationNewPasswordData>();

  protected password = signal('');
  protected confirmPassword = signal('');
  protected hidePassword = signal(true);
  protected hideConfirmPassword = signal(true);

  protected passwordValidationRules = computed(() => {
    const pwd = this.password();
    return [
      { key: 'minLength', label: 'authRegisterCiamRule8Chars', valid: pwd.length >= 8 },
      { key: 'specialChar', label: 'authRegisterCiamRuleSpecialChar', valid: /[^a-zA-Z0-9\s]/.test(pwd) },
      { key: 'letter', label: 'authRegisterCiamRuleLetterChar', valid: /[a-zA-Z]/.test(pwd) },
      { key: 'number', label: 'authRegisterCiamRuleNumber', valid: /[0-9]/.test(pwd) },
    ];
  });

  protected passwordStrengthValid = computed(() => {
    const pwd = this.password();
    if (!pwd) return false;
    return this.passwordValidationRules().every((rule) => rule.valid);
  });

  protected passwordMismatch = computed(() => {
    const pwd = this.password();
    const confirm = this.confirmPassword();
    return confirm.length > 0 && pwd !== confirm;
  });

  protected passwordMatchesEmail = computed(() => {
    const pwd = this.password();
    const email = this.email();
    return pwd.length > 0 && email.length > 0 && pwd.toLowerCase() === email.toLowerCase();
  });

  protected isFormValid = computed(() => {
    return this.passwordStrengthValid() && this.password() === this.confirmPassword() && this.confirmPassword().length > 0 && !this.passwordMatchesEmail();
  });

  onPasswordInput(event: Event): void {
    this.password.set((event.target as HTMLInputElement).value);
  }

  onConfirmPasswordInput(event: Event): void {
    this.confirmPassword.set((event.target as HTMLInputElement).value);
  }

  togglePasswordVisibility(): void {
    this.hidePassword.update((v) => !v);
  }

  toggleConfirmPasswordVisibility(): void {
    this.hideConfirmPassword.update((v) => !v);
  }

  onSubmit(): void {
    if (!this.isFormValid()) return;
    this.newPasswordSubmit.emit({
      email: this.email(),
      newPassword: this.password(),
    });
  }

}
